import { useEffect, useMemo, useRef, useState } from 'react'

import { cn } from '@/lib/utils'

import { useLineChart } from './use-line-chart'

interface Props {
  dataKeys: string[]
  render: () => React.ReactNode
}

export const LineChartTooltip: React.FC<Props> = ({ dataKeys, render }) => {
  const { plotAreaWidth = 0, plotAreaHeight, activeIndex, dataSets, xScale, yScale } = useLineChart()

  const highestPoint = useMemo(() => {
    if (activeIndex === null) {
      return null
    }

    const validValues = dataKeys
      .map((dataKey) => {
        const dataSet = dataSets[dataKey]
        const value = dataSet[activeIndex]

        if (value === null) {
          return null
        }

        return value
      })
      .filter((value) => value !== null)

    if (validValues.length === 0) {
      return null
    }

    const maxValue = Math.max(...validValues)

    const x = xScale(activeIndex)
    const y = yScale(maxValue)

    if (y == null) {
      return null
    }

    return { x, y }
  }, [activeIndex, dataKeys, dataSets, xScale, yScale])

  const [tooltipWidth, setTooltipWidth] = useState(100)

  const x = useMemo(() => {
    if (highestPoint === null) {
      return 0
    }

    if (highestPoint.x - tooltipWidth / 2 < 0) {
      return tooltipWidth / 2
    }

    if (highestPoint.x + tooltipWidth / 2 > plotAreaWidth) {
      return plotAreaWidth - tooltipWidth / 2
    }

    return highestPoint.x
  }, [highestPoint, plotAreaWidth, tooltipWidth])

  if (highestPoint === null) {
    return null
  }

  return (
    <foreignObject x={0} y={0} width={plotAreaWidth} height={plotAreaHeight} style={{ overflow: 'visible' }}>
      <div className={cn('relative h-full w-full', !tooltipWidth && 'opacity-0')}>
        <Tooltip x={x} y={highestPoint.y - 17} onWidth={setTooltipWidth}>
          {render()}
        </Tooltip>
      </div>
    </foreignObject>
  )
}

interface TooltipProps {
  x: number
  y: number
  children: React.ReactNode
  onWidth: (width: number) => void
}

const Tooltip: React.FC<TooltipProps> = ({ x, y, children, onWidth }) => {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (ref.current) {
      onWidth(ref.current.clientWidth)
    }
  }, [onWidth, ref, children])

  return (
    <div
      ref={ref}
      className={cn(
        'absolute -translate-x-1/2 -translate-y-full',
        'shadow-[0px_4px_20px_rgba(0,0,0,0.10)]',
        'inline-block rounded-[15px] p-[15px]',
        'select-none whitespace-nowrap bg-white',
        'duration-150 fade-in'
      )}
      style={{
        top: `${y}px`,
        left: `${x}px`,
      }}
    >
      {children}
    </div>
  )
}
